import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import MergeTypeRounded from "@mui/icons-material/MergeTypeRounded";
import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../components/UIToolkit/Animations/JumpingDots";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { SystemWizardDialog, type WizardStep } from "../../../../components/UIToolkit/Popups/SystemWizardDialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import type { MergeEntryPoint } from "../../../../hooks/useNurseMerge";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { NurseMergeResult } from "../../../../utils/nurseMergeModels";
import { MergeCompareStep } from "./MergeCompareStep";
import { MergeConfirmStep } from "./MergeConfirmStep";
import { MergeDecideStep } from "./MergeDecideStep";
import { MergeFindStep } from "./MergeFindStep";
import { type MergeStepId, useMergeDuplicateFlow } from "./useMergeDuplicateFlow";

type MergeDialogNurse = { id: number; firstName: string; lastName: string };

type Props = {
	/** The record the merge starts from — the one kept unless the person picks the other on Compare. */
	nurse: MergeDialogNurse;
	/**
	 * The other record, when it is already known (a suggested pair). The Find step is then skipped and the
	 * dialog opens on Compare with both records in place; omit it to search for the other record first.
	 */
	pairedNurse?: MergeDialogNurse;
	entryPoint: MergeEntryPoint;
	onClose: () => void;
	onMerged: (result: NurseMergeResult) => void;
};

type Flow = ReturnType<typeof useMergeDuplicateFlow>;

/**
 * Support-only: fold a duplicate staff record into the one worth keeping. Find the other record, compare
 * the two and pick the survivor, answer what only a person can decide, then confirm with the consequences
 * spelled out. Nothing is written until the last step.
 */
export const MergeDuplicateDialog: FC<Props> = ({ nurse, pairedNurse, entryPoint, onClose, onMerged }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const flow = useMergeDuplicateFlow({ currentNurse: nurse, pairedNurseId: pairedNurse?.id, entryPoint, onMerged });

	const steps: WizardStep<MergeStepId>[] = pairedNurse
		? [compareStep(t, flow)]
		: [findStep(t, flow, formatPersonFullName(nurse, lastNameFirst)), compareStep(t, flow)];
	if (flow.hasDecisionStep) steps.push(decideStep(t, flow));
	steps.push(confirmStep(t, flow));

	return (
		<SystemWizardDialog<MergeStepId>
			open
			// Esc / backdrop do nothing while the merge is on the wire — closing would not stop it, only hide the outcome.
			onClose={flow.isMerging ? () => undefined : onClose}
			icon={<MergeTypeRounded />}
			title={t("nurse.merge.title")}
			subtitle={
				pairedNurse
					? t("nurse.merge.subtitle.pair", {
							first: formatPersonFullName(nurse, lastNameFirst),
							firstId: nurse.id,
							second: formatPersonFullName(pairedNurse, lastNameFirst),
							secondId: pairedNurse.id,
						})
					: t("nurse.merge.subtitle", { name: formatPersonFullName(nurse, lastNameFirst), id: nurse.id })
			}
			steps={steps}
			activeStepId={flow.stepId}
			onStepChange={flow.setStepId}
			backLabel={t("back")}
			closeLabel={t("cancel")}
			railAriaLabel={t("nurse.merge.rail")}
			maxWidth="md"
			dataTestId="merge-employees-dialog"
		/>
	);
};

const findStep = (t: TFunction, flow: Flow, currentNurseName: string): WizardStep<MergeStepId> => ({
	id: "find",
	label: t("nurse.merge.step.find"),
	content: (
		<MergeFindStep
			currentNurseName={currentNurseName}
			departmentId={flow.departmentId}
			query={flow.query}
			onQueryChange={flow.setQuery}
			isSettling={flow.isQuerySettling}
			page={flow.candidates.data}
			isLoading={flow.candidates.isFetching}
			isError={flow.candidates.isError}
			onRetry={() => flow.candidates.refetch()}
			onSelect={flow.selectCandidate}
		/>
	),
	primaryLabel: t("nurse.merge.find.continue"),
	onPrimary: () => flow.setStepId("compare"),
	primaryDisabled: flow.candidate === null,
	primaryTooltip: flow.candidate === null ? t("nurse.merge.find.continue.disabled") : undefined,
});

/** Loading / error surface for a step whose body needs the preview. */
const previewState = (t: TFunction, flow: Flow): ReactNode | null => {
	if (flow.previewQuery.isError && !flow.preview) {
		return (
			<EmptyState
				icon={<ErrorOutlineRounded />}
				iconColor="error.main"
				title={t("nurse.merge.preview.error.title")}
				hint={t("nurse.merge.preview.error.hint")}
				action={
					<SystemButton buttonVariant="secondary" size="small" onClick={() => flow.previewQuery.refetch()} data-testid="merge-employees-preview-retry">
						{t("nurse.merge.retry")}
					</SystemButton>
				}
				dataTestId="merge-employees-preview-error"
			/>
		);
	}
	if (!flow.preview) {
		return (
			<Box
				sx={{ display: "flex", justifyContent: "center", py: 6 }}
				role="status"
				aria-label={t("nurse.merge.preview.loading")}
				data-testid="merge-employees-preview-loading"
			>
				<JumpingDots size={6} />
			</Box>
		);
	}
	return null;
};

const compareStep = (t: TFunction, flow: Flow): WizardStep<MergeStepId> => {
	const base = {
		id: "compare" as const,
		label: t("nurse.merge.step.compare"),
		primaryLabel: t("nurse.merge.compare.continue"),
		onPrimary: flow.continueFromCompare,
	};
	const pending = previewState(t, flow);
	if (pending || !flow.preview || !flow.orderedNurseIds) return { ...base, content: pending, primaryDisabled: true };
	return {
		...base,
		content: (
			<MergeCompareStep
				preview={flow.preview}
				orderedNurseIds={flow.orderedNurseIds}
				keepNurseId={flow.keepNurseId}
				onKeepChange={flow.changeKeep}
				isRefreshing={!flow.isPreviewCurrent}
				lookups={flow.lookups}
				timezone={flow.timezone}
			/>
		),
		primaryDisabled: !flow.isPreviewCurrent,
		primaryTooltip: flow.isPreviewCurrent ? undefined : t("nurse.merge.preview.loading"),
	};
};

const decideStep = (t: TFunction, flow: Flow): WizardStep<MergeStepId> => {
	const base = {
		id: "decide" as const,
		label: t("nurse.merge.step.decide"),
		primaryLabel: t("nurse.merge.decide.continue"),
		onPrimary: () => flow.setStepId("confirm"),
	};
	const pending = previewState(t, flow);
	if (pending || !flow.preview) return { ...base, content: pending, primaryDisabled: true };
	const blocked = flow.preview.blockers.length > 0;
	return {
		...base,
		content: (
			<MergeDecideStep preview={flow.preview} choices={flow.choices} onChoicesChange={flow.setChoices} lookups={flow.lookups} timezone={flow.timezone} />
		),
		primaryDisabled: blocked || flow.pendingCount > 0 || !flow.isPreviewCurrent,
		primaryTooltip: blocked
			? t("nurse.merge.decide.blocked.tooltip")
			: flow.pendingCount > 0
				? t("nurse.merge.decide.pending.tooltip", { count: flow.pendingCount })
				: undefined,
	};
};

const confirmStep = (t: TFunction, flow: Flow): WizardStep<MergeStepId> => {
	const base = { id: "confirm" as const, label: t("nurse.merge.step.confirm"), primaryLabel: t("nurse.merge.confirm.button"), onPrimary: flow.confirmMerge };
	const pending = previewState(t, flow);
	if (pending || !flow.preview) return { ...base, content: pending, primaryDisabled: true };
	const cannotMerge = flow.preview.blockers.length > 0 || flow.decisions === null || !flow.isPreviewCurrent;
	return {
		...base,
		content: <MergeConfirmStep preview={flow.preview} choices={flow.choices} lookups={flow.lookups} />,
		primaryDisabled: cannotMerge || flow.isMerging,
		primaryLoading: flow.isMerging,
		primaryTooltip: cannotMerge ? t("nurse.merge.confirm.disabled") : undefined,
		// While the merge is on the wire there is nothing to go back to, and Cancel would not stop it.
		hideBack: flow.isMerging,
		hideClose: flow.isMerging,
	};
};
